import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle2,
  CirclePlay,
  FileSpreadsheet,
} from "lucide-react";
import {
  PROGRESSION_STYLE_LABELS,
  getFramework,
} from "../../config/periodisation";

const SPLIT_LABELS = {
  full_body: "Full Body",
  upper_lower: "Upper / Lower",
  ppl: "Push / Pull / Legs",
  body_part: "Body part split",
};

const frameworkLabel = (p) =>
  getFramework(p.progression_style)?.label ||
  PROGRESSION_STYLE_LABELS[p.progression_style] ||
  p.progression_style;

// #58: the programme library — switch active, edit, delete, or create.
const ProgrammeLibrarySheet = ({
  open,
  onOpenChange,
  programmes,
  activeId,
  onSwitch,
  onEdit,
  onDelete,
  onNew,
  onImport,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleSwitch = (p) => {
    if (p.id === activeId) {
      onOpenChange(false);
      return;
    }
    onSwitch(p);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85dvh] flex flex-col overflow-hidden p-4 sm:p-6">
          <DialogHeader className="shrink-0">
            <DialogTitle>My programmes</DialogTitle>
            <DialogDescription>
              Switch between programmes, edit or remove them. Only one runs at a
              time.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 space-y-2 py-1">
            {programmes.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No programmes yet.
              </p>
            )}

            {programmes.map((p) => {
              const isActive = p.id === activeId;
              const total = Number(p.total_count) || 0;
              const completed = Number(p.completed_count) || 0;
              return (
                <div
                  key={p.id}
                  className={`flex items-stretch gap-1 rounded-lg border transition-colors ${
                    isActive
                      ? "border-primary/50 bg-primary/5"
                      : "border-border/60 bg-muted/10 hover:bg-muted/20"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSwitch(p)}
                    className="flex-1 min-w-0 text-left p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">
                        {p.title}
                      </span>
                      {isActive ? (
                        <Badge className="text-[10px] px-1.5 py-0 bg-primary/20 text-primary border-primary/30 shrink-0">
                          Active
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 shrink-0 text-muted-foreground"
                        >
                          Paused
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {p.source === "imported" ? (
                        "Imported, run as written"
                      ) : (
                        <>
                          {p.focus}
                          {p.split && ` · ${SPLIT_LABELS[p.split] || p.split}`}
                          {frameworkLabel(p) && ` · ${frameworkLabel(p)}`}
                        </>
                      )}
                    </p>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground/80 mt-1.5">
                      <span>
                        {p.days_per_week}×/week · {p.duration_weeks} wks
                      </span>
                      {total > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-secondary" />
                          {completed}/{total}
                        </span>
                      )}
                      {!isActive && (
                        <span className="inline-flex items-center gap-1 text-primary/80">
                          <CirclePlay className="h-3 w-3" /> Switch
                        </span>
                      )}
                    </div>
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Programme actions"
                        className="shrink-0 px-2 self-stretch flex items-center text-muted-foreground/50 hover:text-foreground"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {p.source !== "imported" && (
                        <DropdownMenuItem
                          onClick={() => {
                            onEdit(p);
                            onOpenChange(false);
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setConfirmDelete(p)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>

          <div className="shrink-0 pt-3 mt-1 border-t border-border/40">
            <Button
              className="w-full"
              onClick={() => {
                onNew();
                onOpenChange(false);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New programme
            </Button>
            {onImport && (
              <Button
                variant="outline"
                className="mt-2 w-full"
                onClick={() => {
                  onImport();
                  onOpenChange(false);
                }}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Import a programme
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Programme?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes &ldquo;{confirmDelete?.title}&rdquo; and
              all its session assignments. Completed workout history is
              preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const target = confirmDelete;
                setConfirmDelete(null);
                requestAnimationFrame(() => onDelete(target));
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProgrammeLibrarySheet;
